import type { AxiosRequestConfig } from 'axios';
import { AuthAxios, ResourceAxios } from '@/shared/api/axiosInstance';

function handleError(error: any) {
    console.error("Request failed:", error);
    if (error.response) {
        console.error("Response Data:", error.response.data);
        console.error("Response Status:", error.response.status);
        console.error("Response Headers:", error.response.headers);
    } else if (error.request) {
        console.error("No response received:", error.request);
        console.error('Error message:', error.message);
    } else {
        console.error("Axios error:", error.message);
    }
    throw error;
}

// Automatically determine which Axios instance to use based on the endpoint
function getAxiosInstance(endpoint: string) {
    if (endpoint.startsWith('/authentication')) {
        return AuthAxios; // Authentication requests
    }
    return ResourceAxios; // Resource requests
}

async function getData({
    endpoint,
    config,
}: {
    endpoint: string;
    config?: AxiosRequestConfig<any>;
}): Promise<any> {
    try {
        const axiosInstance = getAxiosInstance(endpoint);
        const response = await axiosInstance.get(endpoint, config);
        return response;
    } catch (error: any) {
        handleError(error);
        return error;
    }
}

async function postData({
    endpoint,
    data,
    config,
}: {
    endpoint: string;
    data: any;
    config?: AxiosRequestConfig<any>;
}) {
    try {
        const axiosInstance = getAxiosInstance(endpoint);
        const response = await axiosInstance.post(endpoint, data, config);
        return response;
    } catch (error: any) {
        handleError(error);
        return error;
    }
}

async function putData({
    endpoint,
    data,
    config,
}: {
    endpoint: string;
    data: any;
    config?: AxiosRequestConfig<any>;
}) {
    try {
        const axiosInstance = getAxiosInstance(endpoint);
        const response = await axiosInstance.put(endpoint, data, config);
        return response;
    } catch (error: any) {
        handleError(error);
        return error;
    }
}

async function patchData({
    endpoint,
    data,
    config,
}: {
    endpoint: string;
    data: any;
    config?: AxiosRequestConfig<any>;
}) {
    try {
        const axiosInstance = getAxiosInstance(endpoint);
        const response = await axiosInstance.patch(endpoint, data, config);
        return response;
    } catch (error: any) {
        handleError(error);
        return error;
    }
}

async function deleteData({
    endpoint,
    config,
}: {
    endpoint: string;
    config?: AxiosRequestConfig<any>;
}): Promise<any> {
    try {
        const axiosInstance = getAxiosInstance(endpoint);
        const response = await axiosInstance.delete(endpoint, config);
        return response;
    } catch (error: any) {
        handleError(error);
        return error;
    }
}

async function deleteDataWithPayload({
  endpoint,
  data,
  config,
}: {
  endpoint: string;
  data?: any; 
  config?: AxiosRequestConfig<any>;
}): Promise<any> {
  try {
    const axiosInstance = getAxiosInstance(endpoint);

    const response = await axiosInstance.delete(endpoint, {
      ...config,
      data,
    });

    return response;
  } catch (error: any) {
    handleError(error);
    return error;
  }
}

export { deleteData, getData, patchData, postData, putData, deleteDataWithPayload };
